import { ReactElement } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  IconButton,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIconButton from "../../../components/DeleteIconButton";
import { TransactionCategory, TransactionCategoryGroup } from "../../../types";

interface SortableGroupCardProps {
  group: TransactionCategoryGroup;
  inUse: boolean;
  categoriesUsingGroup: TransactionCategory[];
  onEdit: (group: TransactionCategoryGroup) => void;
  onDelete: (group: TransactionCategoryGroup) => void;
  onDetails: (group: TransactionCategoryGroup) => void;
}

function SortableGroupCard({
  group,
  inUse,
  categoriesUsingGroup,
  onEdit,
  onDelete,
  onDetails,
}: SortableGroupCardProps): ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 4 : 0}
      sx={{
        p: 1,
        border: 1,
        borderColor: "divider",
        borderRadius: 0.5,
        "&:hover": { boxShadow: 1 },
        transition: "box-shadow 0.2s",
        cursor: isDragging ? "grabbing" : "grab",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: isDragging ? "grabbing" : "grab",
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
            mt: 0.25,
            touchAction: "none", // so touch starts drag, not scroll
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>

        {/* Group Name */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
            {group.name}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onDetails(group)}
            sx={{ minWidth: "auto", height: "32px", px: 1.5 }}
          >
            Manage Details
          </Button>
          <Chip
            label={group.details && group.details.length > 0 ? group.details.length : "N/A"}
            size="small"
            sx={{
              height: "24px",
              width: "24px",
              borderRadius: "50%",
              fontSize: "0.65rem",
              "& .MuiChip-label": {
                px: 0,
                py: 0,
              },
            }}
          />
          <IconButton
            size="small"
            onClick={() => onEdit(group)}
            sx={{ color: "primary.main" }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <DeleteIconButton
            onClick={() => onDelete(group)}
            disabled={inUse}
            disabledTooltip={`Cannot delete group "${group.name}": It is used by one or more categories`}
            size="small"
          />
        </Stack>
      </Stack>
    </Paper>
  );
}

export default SortableGroupCard;
